import React, { useState, useEffect, useRef } from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Paper,
  IconButton,
  Tooltip,
  TextField,
  Box,
  Skeleton,
  Typography
} from '@mui/material';
import {
  Edit as EditIcon,
  Delete as DeleteIcon,
  Search as SearchIcon
} from '@mui/icons-material';
import ListAltIcon from '@mui/icons-material/ListAlt';
import FolderOffIcon from '@mui/icons-material/FolderOff';
import SyncIcon from '@mui/icons-material/Sync';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import { MONO_FONT } from '../constants/typography';
import useDebouncedValue from '../hooks/useDebouncedValue';
import { isSyncable } from '../utils/registrySourceConfig';

const RegistryTable = ({
  schema,
  data,
  totalCount,
  page,
  rowsPerPage,
  onPageChange,
  onRowsPerPageChange,
  onEdit,
  onDelete,
  onSearch,
  onViewEntries,
  onSync,
  loading = false,
  searchDebounceMs = 300,
  emptyTitle = 'Nessun elemento',
  emptyHint = ''
}) => {
  const [searchTerm, setSearchTerm] = useState('');

  const debouncedSearch = useDebouncedValue(searchTerm, searchDebounceMs);

  // onSearch riceve il termine "debounced", mai al mount
  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) { firstSearch.current = false; return; }
    if (onSearch) onSearch(debouncedSearch);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch]);

  const handleSearch = (event) => {
    setSearchTerm(event.target.value);
  };

  const getVisibleColumns = () => {
    return Object.entries(schema.properties)
      .filter(([_, field]) => field.visible_on_ui)
      .map(([fieldName, field]) => ({
        field: fieldName,
        headerName: field.name,
        type: field.type,
        searchable: field.searchable
      }));
  };

  const renderCell = (field, value) => {
    if (field.relationship) {
      const option = field.relationship.options?.find(opt => opt.value === value);
      return option ? option.label : value;
    }

    switch (field.type) {
      case 'boolean':
        return (
          <Typography
            component="span"
            variant="body2"
            sx={{ color: value ? 'text.primary' : 'text.secondary', fontWeight: value ? 600 : 400 }}
          >
            {value ? 'Sì' : 'No'}
          </Typography>
        );
      case 'date':
        return value ? new Date(value).toLocaleDateString() : '';
      case 'datetime':
        return value ? new Date(value).toLocaleString() : '';
      case 'time':
        return value ? new Date(value).toLocaleTimeString() : '';
      default:
        if (field.mono && value != null && value !== '') {
          return (
            <Box
              component="span"
              sx={{ fontFamily: MONO_FONT, fontSize: '0.8125rem', color: 'primary.main' }}
            >
              {value}
            </Box>
          );
        }
        return value == null || value === ''
          ? <Box component="span" sx={{ color: 'text.disabled' }}>—</Box>
          : value;
    }
  };

  const columns = getVisibleColumns();
  const colSpan = columns.length + 1;

  return (
    <Paper elevation={2}>
      <Box sx={{ p: 2, display: 'flex', alignItems: 'center' }}>
        <TextField
          size="small"
          placeholder="Cerca..."
          value={searchTerm}
          onChange={handleSearch}
          InputProps={{
            startAdornment: <SearchIcon sx={{ mr: 1, color: 'text.secondary' }} />
          }}
          sx={{ width: 300 }}
        />
      </Box>
      
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              {columns.map((column) => (
                <TableCell key={column.field}>
                  {column.headerName}
                </TableCell>
              ))}
              <TableCell align="right">Azioni</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              Array.from({ length: rowsPerPage && rowsPerPage < 6 ? rowsPerPage : 5 }).map((_, r) => (
                <TableRow key={`skeleton-${r}`}>
                  {columns.map((column) => (
                    <TableCell key={column.field}>
                      <Skeleton variant="text" width={`${60 + ((r * 7 + column.field.length * 5) % 35)}%`} />
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    <Skeleton variant="text" width={72} sx={{ ml: 'auto' }} />
                  </TableCell>
                </TableRow>
              ))
            ) : data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={colSpan} sx={{ borderBottom: 'none', py: 7 }}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
                    <FolderOffIcon sx={{ fontSize: 44, opacity: 0.35, mb: 0.5 }} />
                    <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'text.primary' }}>
                      {emptyTitle}
                    </Typography>
                    {emptyHint && <Typography variant="body2">{emptyHint}</Typography>}
                  </Box>
                </TableCell>
              </TableRow>
            ) : (
              data.map((row, index) => (
                <TableRow key={index} hover>
                  {columns.map((column) => (
                    <TableCell key={column.field}>
                      {renderCell(column, row[column.field])}
                    </TableCell>
                  ))}
                  <TableCell align="right">
                    {onViewEntries && (
                      <Tooltip title="Voci">
                        <IconButton
                          aria-label="voci"
                          size="small"
                          onClick={() => onViewEntries(row)}
                        >
                          <ListAltIcon fontSize="small" />
                        </IconButton>
                      </Tooltip>
                    )}
                    {/* Un comando senza handler non si mostra: chi non ha il permesso non lo
                        vede, invece di vederlo e prendere un 403. */}
                    {onSync && isSyncable(row) && (
                      <Tooltip title={row.sourceType === 'csv' ? 'Carica CSV' : 'Sincronizza'}>
                        <IconButton
                          aria-label={row.sourceType === 'csv' ? 'Carica CSV' : 'Sincronizza'}
                          size="small"
                          onClick={() => onSync(row)}
                        >
                          {row.sourceType === 'csv' ? <UploadFileIcon fontSize="small" /> : <SyncIcon fontSize="small" />}
                        </IconButton>
                      </Tooltip>
                    )}
                    {onEdit && (
                      <Tooltip title="Modifica">
                        <IconButton
                          aria-label="Modifica"
                          size="small"
                          onClick={() => onEdit(row)}
                        >
                          <EditIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                    {onDelete && (
                      <Tooltip title="Elimina">
                        <IconButton
                          aria-label="Elimina"
                          size="small"
                          onClick={() => onDelete(row)}
                        >
                          <DeleteIcon />
                        </IconButton>
                      </Tooltip>
                    )}
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>
      
      {(loading || data.length > 0) && (
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={onPageChange}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={onRowsPerPageChange}
          rowsPerPageOptions={[5, 10, 25, 50]}
          labelRowsPerPage="Righe per pagina:"
          labelDisplayedRows={({ from, to, count }) =>
            `${from}-${to} di ${count}`
          }
        />
      )}
    </Paper>
  );
};

export default RegistryTable; 